import * as React from 'react';
import { useSelector } from 'react-redux';

import { useDesktopListingOverlayWidth } from './useDesktopListingOverlayWidth';
import { selectIsDesktopListingVisible } from '../../../../common/selectors/desktopListing';
import { isCenterZoomBounds } from '../../../../common/utils/mapBounds';
import { useMapController } from '../../../controller/useMapController';
import { selectMapBounds } from '../../../selectors';

import type { IRBushItem } from '../../../../common/types/map';
import type { IReserveAreaParams, TInitialFitView } from '../../../controller/types';
import type { TMapBounds } from '../../../types/mapBounds';
import type RBush from 'rbush';

interface IUseListingAreaReservationParams {
  rbush: RBush<IRBushItem>;
}

function getVisibleFeatureCoordinates(rbush: RBush<IRBushItem>): YMaps.TCoord[] | undefined {
  const coordinates = rbush.all().map(({ feature }) => feature.geometry.coordinates as YMaps.TCoord);

  return coordinates.length > 0 ? coordinates : undefined;
}

function getInitialFitView(isInitialEffect: boolean, mapBounds: TMapBounds): TInitialFitView | undefined {
  // ЭКСПЕРИМЕНТ: fitView только для bbox (useBoundsSync на initial+bbox делает return - вписать некому).
  // center+zoom на initial вписывает useBoundsSync.focusOn под margin - там fitView был бы дублем.
  if (!isInitialEffect || !mapBounds.bounds) {
    return undefined;
  }

  return { bounds: mapBounds.bounds };
}

function buildReserveParams(
  base: IReserveAreaParams,
  initialFitView: TInitialFitView | undefined,
  visibleFeatureCoordinates: YMaps.TCoord[] | undefined,
): IReserveAreaParams {
  if (initialFitView) {
    return { ...base, fitView: initialFitView };
  }

  if (visibleFeatureCoordinates) {
    return { ...base, visibleFeatureCoordinates };
  }

  return base;
}

export function useListingAreaReservation({ rbush }: IUseListingAreaReservationParams): number {
  const controller = useMapController();
  const mapBounds = useSelector(selectMapBounds);
  const isDesktopListingVisible = useSelector(selectIsDesktopListingVisible);
  const listingAreaWidth = useDesktopListingOverlayWidth();
  const [settledListingAreaWidth, setSettledListingAreaWidth] = React.useState(listingAreaWidth);
  const shouldReserveListingArea = listingAreaWidth > 0;
  const initialMapBoundsRef = React.useRef(mapBounds);
  const isInitialEffectRef = React.useRef(true);
  const previousListingVisibleRef = React.useRef(isDesktopListingVisible);
  const currentListingVisibleRef = React.useRef(isDesktopListingVisible);
  const currentListingAreaWidthRef = React.useRef(listingAreaWidth);
  const isMountedRef = React.useRef(false);
  const operationIdRef = React.useRef(0);
  const rbushRef = React.useRef(rbush);

  currentListingVisibleRef.current = isDesktopListingVisible;
  currentListingAreaWidthRef.current = listingAreaWidth;
  rbushRef.current = rbush;

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      operationIdRef.current += 1;
    };
  }, []);

  const applySettledListingAreaWidth = React.useCallback((width: number, operationId: number) => {
    if (!isMountedRef.current || operationIdRef.current !== operationId) {
      return;
    }

    setSettledListingAreaWidth(width);
  }, []);

  React.useEffect(() => {
    const isInitialEffect = isInitialEffectRef.current;
    const hasListingVisibilityChanged = previousListingVisibleRef.current !== isDesktopListingVisible;
    isInitialEffectRef.current = false;
    previousListingVisibleRef.current = isDesktopListingVisible;

    if (!shouldReserveListingArea) {
      return undefined;
    }

    const reserveOperationId = operationIdRef.current + 1;
    operationIdRef.current = reserveOperationId;
    // initial bbox вписываем в видимую (margin) область сразу (fitView) - иначе вид остался бы под
    // полной шириной. initial center+zoom центрирует useBoundsSync.focusOn, тут ни fit, ни restore.
    // Динамика (открыл/закрыл листинг) - сохраняем видимый охват (restoreVisibleArea).
    const initialFitView = getInitialFitView(isInitialEffect, initialMapBoundsRef.current);
    const shouldSkipInitialRestore = isInitialEffect && isCenterZoomBounds(initialMapBoundsRef.current);
    const shouldRestoreVisibleArea =
      !initialFitView && !shouldSkipInitialRestore && (isInitialEffect || hasListingVisibilityChanged);
    const reserveParams: IReserveAreaParams = {
      onApplied: () => applySettledListingAreaWidth(listingAreaWidth, reserveOperationId),
      side: 'left',
      width: listingAreaWidth,
      restoreVisibleArea: shouldRestoreVisibleArea,
    };
    const visibleFeatureCoordinates = shouldRestoreVisibleArea
      ? getVisibleFeatureCoordinates(rbushRef.current)
      : undefined;
    const handle = controller.reserveAreaWithoutReload(
      buildReserveParams(reserveParams, initialFitView, visibleFeatureCoordinates),
    );

    return () => {
      const releaseOperationId = operationIdRef.current + 1;
      operationIdRef.current = releaseOperationId;
      const nextListingAreaWidth = currentListingAreaWidthRef.current;
      const onApplied = () => applySettledListingAreaWidth(nextListingAreaWidth, releaseOperationId);
      const shouldRestoreOnRelease = currentListingVisibleRef.current !== isDesktopListingVisible;
      const nextVisibleFeatureCoordinates = shouldRestoreOnRelease
        ? getVisibleFeatureCoordinates(rbushRef.current)
        : undefined;

      if (nextVisibleFeatureCoordinates) {
        controller.releaseAreaWithoutReload(handle, {
          onApplied,
          visibleFeatureCoordinates: nextVisibleFeatureCoordinates,
        });

        return;
      }

      if (!shouldRestoreOnRelease) {
        controller.releaseAreaWithoutReload(handle, { onApplied, restoreVisibleArea: false });

        return;
      }

      controller.releaseAreaWithoutReload(handle, { onApplied });
    };
  }, [applySettledListingAreaWidth, controller, shouldReserveListingArea, listingAreaWidth, isDesktopListingVisible]);

  return settledListingAreaWidth;
}
